import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

import {
    phaseEstimateMs,
    phaseProgress,
    phaseTimerKey,
    type OperationDirection,
} from '../operationPhase';

const TICK_MS = 500;

interface OperationProgressProps {
    direction: OperationDirection;
    /**
     * Backend job step. This is also the "work has actually started" signal: a step only exists
     * once the job is accepted and running, so the bar cannot begin while a wallet signature
     * prompt is still open.
     */
    step: string | null | undefined;
    /**
     * The user's chain for origin-chain steps (a deposit's source). Cronos and Robinhood have very
     * different block times, and only the caller knows which one this operation is on.
     */
    chainId?: number | null;
    className?: string;
}

/**
 * Per-phase progress estimate for a deposit, withdrawal or migration.
 *
 * The timer restarts on every phase change, because each phase is its own transaction: a
 * withdrawal that sweeps HyperCore, flushes the adapter and then bridges is three separate waits,
 * and one bar spanning all of them would be wrong for each.
 *
 * Bar only — no elapsed/estimate text. The label and spinner above it already say what is
 * happening; a second line repeating "about 75s" just competes with them.
 */
const OperationProgress: React.FC<OperationProgressProps> = ({
    direction,
    step,
    chainId = null,
    className,
}) => {
    const stepEstimateMs = phaseEstimateMs(step, { chainId });

    // the phase identity: when it changes, this is a NEW transaction and the bar starts over.
    // Steps that share a timer key (the two halves of the HyperCore funding) keep the start AND
    // the estimate the stage began with, so the bar runs on through them.
    const phaseKey = phaseTimerKey(step);
    const startedAtRef = useRef<{
        key: string;
        at: number;
        estimateMs: number | null;
    }>({
        key: phaseKey,
        at: Date.now(),
        estimateMs: stepEstimateMs,
    });
    if (startedAtRef.current.key !== phaseKey) {
        startedAtRef.current = {
            key: phaseKey,
            at: Date.now(),
            estimateMs: stepEstimateMs,
        };
    } else if (
        startedAtRef.current.estimateMs === null &&
        stepEstimateMs !== null
    ) {
        // the chain (or the step's own budget) arrived a render later
        startedAtRef.current.estimateMs = stepEstimateMs;
    }
    const estimateMs = startedAtRef.current.estimateMs;

    const [nowMs, setNowMs] = useState(() => Date.now());
    useEffect(() => {
        if (!estimateMs) return;
        const timer = setInterval(() => setNowMs(Date.now()), TICK_MS);
        return () => clearInterval(timer);
    }, [estimateMs, phaseKey]);

    if (!estimateMs) {
        return null;
    }

    const { percent, overrun } = phaseProgress(
        startedAtRef.current.at,
        nowMs,
        estimateMs,
    );

    return (
        <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(percent)}
            aria-label={`${direction} progress`}
            className={cn(
                'h-1 w-full overflow-hidden rounded-full bg-primary/15',
                className,
            )}
        >
            <div
                className={cn(
                    'h-full rounded-full bg-primary/70 transition-[width] duration-500 ease-linear',
                    // past the estimate the bar cannot advance any further, so it pulses instead —
                    // the movement is what tells the user it is still working, now that the
                    // "still working" copy is gone.
                    overrun && 'animate-pulse',
                )}
                style={{ width: `${percent}%` }}
            />
        </div>
    );
};

OperationProgress.displayName = 'OperationProgress';

export default OperationProgress;
